import { flattenDeep, isEmpty, keyBy } from 'lodash';

import { CLIENTS } from './navigation/clients';

import Sidebar from '@commons/Sidebar';
import { ModuleContainer, PageContainer } from './styledComponents';
import { ModuleProps } from '../types/module';
import { JSX } from 'react';

interface ModuleInfo {
  name: string | unknown;
  breadCrumbName: string | unknown;
}

interface SubModuleInfo {
  name: string | unknown;
  breadCrumbName: string | unknown;
  isVisible: (props: unknown) => boolean; // Adjust 'any' to the actual type of 'props'
  hideSubmoduleName?: boolean;
}

interface NavTreeItem {
  path: string;
  moduleInfo: ModuleInfo;
  subModuleInfo: SubModuleInfo;
  parentSubFeaturesPath: string | null;
}

const ENUM_MODULES = {
  CLIENTS
};

export type Module = {
  path: string;
  Component: (props: ModuleProps) => JSX.Element;
  getText?: () => string;
  getBreadCrumbText?: () => string;
  isVisible?: (props: unknown) => boolean;
  isFullScreen?: boolean;
  icon?: string;
  children?: Module[];
  subFeatures?: Module[];
  extraText?: (props: Record<string, unknown>) => string;
};

export const ORDERED_NAV_TREE: Module[] = [ENUM_MODULES.CLIENTS];

export const getNavTree = () =>
  ORDERED_NAV_TREE.map(({ path, Component, getText, isFullScreen, subFeatures, extraText }) => ({
    path: path.substring(1),
    element: (
      <ModuleContainer>
        {!isFullScreen && <Sidebar />}
        <PageContainer>
          <Component
            extraText={extraText}
            getText={getText}
            path={path}
            subFeatures={subFeatures}
          />
        </PageContainer>
      </ModuleContainer>
    )
  }));

/**
 * Recursive function to browse the navigation tree and get all features and subFeatures while gathering
 * moduleInfo and subModuleInfo while browsing
 *
 * @param {object} feature
 * @param {object} moduleInfo
 * @param {object} subModuleInfo
 * @param {String | null} parentSubFeaturesPath
 *
 * @returns {[]} The list of all features and subModules with moduleInfo and subModuleInfo
 */
const _getModuleNavTree = (
  feature: Module,
  moduleInfo: ModuleInfo = { name: '', breadCrumbName: '' },
  subModuleInfo: SubModuleInfo = { name: '', breadCrumbName: '', isVisible: () => true },
  parentSubFeaturesPath: string | null = null
): object => {
  if (!feature.children) {
    // Some features have subFeatures, only displayed in the NavBar
    if (!feature.subFeatures) {
      return { ...feature, moduleInfo, subModuleInfo, parentSubFeaturesPath };
    }

    const subFeatures = feature.subFeatures.map((subFeature) =>
      _getModuleNavTree(subFeature, moduleInfo, subModuleInfo, feature.path)
    ) as NavTreeItem[];

    return [{ ...feature, moduleInfo, subModuleInfo }, ...subFeatures];
  }

  // If moduleInfo is empty, the current feature is a module, so we generate moduleInfo
  if (isEmpty(moduleInfo)) {
    return feature.children.map((child) =>
      _getModuleNavTree(
        child,
        {
          name: feature.getText?.(),
          breadCrumbName: feature.getBreadCrumbText?.() || feature.getText?.() || ''
        },
        subModuleInfo
      )
    ) as NavTreeItem[];
  }

  // If subModuleInfo is empty, the current feature is a subModule, so we generate subModuleInfo
  if (isEmpty(subModuleInfo)) {
    return feature.children.map((child) =>
      _getModuleNavTree(child, moduleInfo, {
        name: feature.getText?.(),
        breadCrumbName: feature.getBreadCrumbText
          ? feature.getBreadCrumbText()
          : feature.getText?.(),
        // If the subModule feature does not have isVisible prop, it is visible by default
        isVisible: (props) => feature.isVisible?.(props) || true
      })
    ) as NavTreeItem[];
  }

  return feature.children.map((child) => _getModuleNavTree(child, moduleInfo, subModuleInfo));
};

export const getNavTreeKeyByPath = ({ navTree }: { navTree: Module[] }) => {
  const flattenedNavTree = flattenDeep(navTree.map((module) => _getModuleNavTree(module)));

  const keyByPathNavTree = keyBy(flattenedNavTree, 'path');

  return keyByPathNavTree;
};

/**
 * Get a feature that has subFeatures given a path
 *
 * @param {object} keyByPathNavTree - Navigation tree keyed by path
 * @param {string} path - Current user location
 * @returns {Module | null}
 */
export const getSubFeaturesForPath = (keyByPathNavTree: object, path: string): Module | null => {
  const feature = keyByPathNavTree[path as keyof object];

  if (!feature || !keyByPathNavTree[feature['parentSubFeaturesPath'] as keyof object]) {
    return null;
  }

  return keyByPathNavTree[feature['parentSubFeaturesPath'] as keyof object];
};
